import React, { memo, useState, useMemo } from "react";
import { Button } from "@material-ui/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { RailcarItem } from "./RailcarItem";

/**
 * Optimized Railcar List component for Crew App to isolate track re-renders
 * Renders a list of railcar items within a sortable context for drag-and-drop
 * 
 * Uses grouped props pattern for cleaner API:
 * - handlers: Action callbacks
 * - uiConfig: UI configuration
 * - selectionState: Selection and filter state
 * - context: Contextual data
 */
export const TrackRailcarList = memo(({
  trackCars,
  fullCarsOnTrack,
  trackLocationId,
  isDraggingGlobal = false,
  onMouseEnter,
  onMouseLeave,
  // Stable props for rendering
  selectedIdsSet,
  getShipmentClassName,
  getShipmentIcon,
  classes,
  canDelete,
  canAdd,
  canOpenShipment,
  isXSmall,
  openShipment,
  onShipmentClick,
  onShipmentLongPress,
  accountFilterId,
  handleDeleteConfirm,
  removeFromTrack,
  addItem,
  showPlaceAction,
  defaultShipmentId,
  currentShipment,
  selectedShipment,
  colorsLoaded,
}) => {
  // Crew optimization: Mobile windowing (smaller limit for mobile)
  const [limit, setLimit] = useState(30);
  const hasMore = trackCars.length > limit;
  const visibleCars = hasMore ? trackCars.slice(0, limit) : trackCars;

  // STAFF LEVEL OPTIMIZATION: Pre-calculate index map to avoid O(N^2) in the render loop
  const indexMap = useMemo(() => {
    const map = new Map();
    fullCarsOnTrack.forEach((car, i) => map.set(car.id, i));
    return map;
  }, [fullCarsOnTrack]);

  // Staff Level Optimization: Memoize ID array to prevent @dnd-kit re-indexing lag.
  const sortableIds = useMemo(() => trackCars.map(car => car.id), [trackCars]);

  // Group props for RailcarItem - memoized for stable references
  const handlers = useMemo(() => ({
    openShipment,
    onShipmentClick,
    onShipmentLongPress,
    handleDeleteConfirm,
    removeFromTrack,
    addItem,
    onMouseEnter,
    onMouseLeave,
  }), [openShipment, onShipmentClick, onShipmentLongPress, handleDeleteConfirm, removeFromTrack, addItem, onMouseEnter, onMouseLeave]);

  const uiConfig = useMemo(() => ({
    getShipmentClassName,
    getShipmentIcon,
    isXSmall,
    showPlaceAction,
    colorsLoaded,
  }), [getShipmentClassName, getShipmentIcon, isXSmall, showPlaceAction, colorsLoaded]);

  const context = useMemo(() => ({
    defaultShipmentId,
    currentShipment,
    selectedShipment,
  }), [defaultShipmentId, currentShipment, selectedShipment]);

  return (
    <SortableContext
      items={sortableIds}
      strategy={verticalListSortingStrategy}
    >
      {visibleCars.map((trackCar) => {
        const realIdx = indexMap.get(trackCar.id) ?? -1;
        const isSelected = selectedIdsSet.has(trackCar.id);
        
        // Create selectionState per item (isSelected varies per item)
        // canOpenShipment is consumed by RailcarItem for both cursor and click gating.
        const selectionState = {
          isSelected,
          canDelete,
          canAdd,
          accountFilterId,
          canOpenShipment,
        };

        return (
          <RailcarItem
            key={trackCar.id}
            shipment={trackCar}
            index={realIdx}
            trackLocationId={trackLocationId}
            isOverlay={false}
            isDraggingGlobal={isDraggingGlobal}
            handlers={handlers}
            uiConfig={uiConfig}
            selectionState={selectionState}
            context={context}
          />
        );
      })}
      {hasMore && (
        <Button 
          fullWidth 
          variant="outlined"
          size="small" 
          onClick={(e) => { e.stopPropagation(); setLimit(prev => prev + 50); }}
          style={{ marginTop: 8, marginBottom: 8 }}
        >
          Show More ({trackCars.length - limit})
        </Button>
      )}
    </SortableContext>
  );
});

TrackRailcarList.displayName = "TrackRailcarList";
